import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { loginPathFor } from '@/lib/admin-access';
import { useAdminSession } from './admin-session';

/**
 * The console's front door, and what `middleware.ts` used to be.
 *
 * The Next.js version split this in two: middleware bounced a request with no
 * cookie, and the layout asked the API whether the session was real. There is
 * only one half here, and it is the second one — the cheap cookie check is
 * impossible now (the cookie is httpOnly and this code cannot read it) and
 * unnecessary, because restoring the session is one request the app makes at
 * startup anyway.
 *
 * **This is not the authorization boundary.** Every guarded call re-checks the
 * grant server-side (ADR-007), and a forged local state gets a 401 rather than
 * data. What this decides is which screen to render, so an admin never watches
 * the shell paint and then vanish.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { state } = useAdminSession();
  const location = useLocation();

  // Nothing, deliberately, rather than a spinner. Restoring is one request on a
  // connection an admin console is used over; a spinner that appears for 80ms
  // and disappears reads as a flicker, and the alternative is the login form
  // flashing at somebody who is signed in.
  if (state.status === 'restoring') {
    return null;
  }

  if (state.status === 'signedOut') {
    return <Navigate to={loginPathFor(`${location.pathname}${location.search}`)} replace />;
  }

  return <>{children}</>;
}
